import React from "react";
import { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { withTheme } from "@emotion/react";
import { NavLink, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Bar } from "react-chartjs-2";

import {
  Briefcase,
  DollarSign,
  ExternalLink,
  Facebook,
  Home,
  Instagram,
  MapPin,
  ShoppingBag,
  Twitter
} from "react-feather";

import {
  Avatar as MuiAvatar,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button as MuiButton,
  Card as MuiCard,
  CardContent,
  Chip as MuiChip,
  Divider as MuiDivider,
  Grid as MuiGrid,
  LinearProgress as MuiLinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography as MuiTypography,
  Stepper,
  Stack,
  Step,
  StepLabel,
  CircularProgress,
  tooltipClasses,
  TooltipProps,
  Tooltip,
  IconButton,
  Alert,
  ListItem,
  List,
  ListItemButton,
  ListItemIcon,
  Checkbox,
  ListItemText,
  Pagination
} from "@mui/material";
import { spacing, SpacingProps } from "@mui/system";
import { ThemeProps } from "../../../types/theme";
import { useTranslation } from "react-i18next";
import FirstTimeLoginDialog from "../../components/FirstTimeLoginDialog";
import { DialogMode } from "../../../types/dialogmode";
import FilesToUploadList from "../../../components/lists/FilesToUploadList";
import { ManualFileUploader } from "../../../components/dialogs/InitiateDocumentsDialog";
import { IDocument } from "../../../types/document";
import { ContractStatus, IContract } from "../../../types/contract";
import {
  useAddContractMutation,
  useAddDocumentContentMutation,
  useAddDocumentMutation,
  useAddPromptMutation,
  useGetDefaultPromptsQuery,
  useGetPromptsQuery
} from "../../../redux/slices/indexApiSlice";
import useAuth from "../../../hooks/useAuth";
import { IPrompt } from "../../../types/prompt";
import { Info } from "@mui/icons-material";
import BreadCrumbsBar from "../../../components/breadcrumbsBar/BreadCrumbsBar";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Button = styled(MuiButton)(spacing);

const Card = styled(MuiCard)(spacing);

const Chip = styled(MuiChip)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Grid = styled(MuiGrid)(spacing);

const LinearProgress = styled(MuiLinearProgress)(spacing);

const Spacer = styled.div(spacing);

interface TypographyProps extends SpacingProps {
  component?: string;
}
const Typography = styled(MuiTypography)<TypographyProps>(spacing);

const Centered = styled.div`
  text-align: center;
`;

const Avatar = styled(MuiAvatar)`
  display: inline-block;
  height: 128px;
  width: 128px;
`;

const AboutIcon = styled.span`
  display: flex;
  padding-right: ${(props) => props.theme.spacing(2)};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ChartWrapper = styled.div`
  height: 280px;
  position: relative;
`;

const StatsIcon = styled.div`
  position: absolute;
  right: 16px;
  top: 32px;

  svg {
    width: 32px;
    height: 32px;
    color: ${(props) => props.theme.palette.secondary.main};
  }
`;

const ProductsChip = styled(Chip)<{ color?: string }>`
  height: 20px;
  padding: 4px 0;
  font-size: 90%;
  background-color: ${(props) =>
    props.theme.palette[props.color ? props.color : "primary"].light};
  color: ${(props) => props.theme.palette.common.white};
`;

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)});
`;

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "transparent" // theme.palette.common.white,
  }
}));

export const PromptsSelector: React.FC<{
  defaultPrompts?: IPrompt[];
  selectedPrompts: IPrompt[];
  setSelectedPrompts: React.Dispatch<React.SetStateAction<IPrompt[]>>;
}> = ({ defaultPrompts, selectedPrompts, setSelectedPrompts }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const pageSize = 10;
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil((defaultPrompts?.length || 0) / pageSize);

  const handlePromptSelection = (prompt: IPrompt) => {
    if (selectedPrompts.includes(prompt)) {
      setSelectedPrompts((prev) => prev.filter((p) => p.id !== prompt.id));
    } else {
      if (selectedPrompts.length >= (user?.account?.promptsLibraryLimit || 0)) {
        return;
      }

      setSelectedPrompts((prev) => [...prev, prompt]);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="body1"
        gutterBottom
        color="textSecondary"
        alignItems="center"
        display="flex"
        gap={1}
      >
        {t("firstTimeLoginDialog.welcomeMessage")}

        <LightTooltip
          title={
            <React.Fragment>
              <Card variant="outlined">
                <CardContent>
                  <Typography gutterBottom variant="body2" component="div">
                    {t("firstTimeLoginDialog.step_selectPrompts_moreInfo")}
                  </Typography>
                </CardContent>
              </Card>
            </React.Fragment>
          }
          placement="top-start"
        >
          <IconButton size="small">
            <Info fontSize="small" />
          </IconButton>
        </LightTooltip>
      </Typography>

      {user?.account?.promptsLibraryLimit &&
      selectedPrompts?.length >= user?.account?.promptsLibraryLimit ? (
        <Alert severity="success" sx={{ mt: 2 }}>
          {t("firstTimeLoginDialog.promptLimitReached", {
            count: user?.account?.promptsLibraryLimit
          })}
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t("firstTimeLoginDialog.step_selectPrompts_description", {
            count: user?.account?.promptsLibraryLimit
          })}
        </Alert>
      )}

      <List sx={{ width: "100%" }} disablePadding dense>
        {defaultPrompts?.map((prompt, i) => {
          if (i >= pageSize * page || i < pageSize * (page - 1)) return;
          else
            return (
              <ListItem key={prompt.id} disablePadding>
                <ListItemButton
                  onClick={() => {
                    handlePromptSelection(prompt);
                  }}
                  dense
                  disabled={
                    !selectedPrompts.includes(prompt) &&
                    selectedPrompts.length >=
                      (user?.account?.promptsLibraryLimit || 0)
                  }
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedPrompts.includes(prompt)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(prompt.title || "")}
                    secondary={
                      <LightTooltip
                        title={
                          <React.Fragment>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography
                                  gutterBottom
                                  variant="body2"
                                  component="div"
                                >
                                  {prompt.body}
                                </Typography>
                              </CardContent>
                            </Card>
                          </React.Fragment>
                        }
                        placement="right-end"
                      >
                        <Typography
                          component="span"
                          variant="body2"
                          color="textDisabled"
                          sx={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {prompt.body}
                        </Typography>
                      </LightTooltip>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
        })}
      </List>

      <div>
        <Pagination
          count={totalPages}
          defaultPage={1}
          onChange={(e: any, p: number) => setPage(p)}
          sx={{ mt: 2, display: "flex", justifyContent: "center" }}
        />
      </div>
    </Box>
  );
};

function Details() {
  return (
    <Card mb={6}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Profile Details
        </Typography>

        <Spacer mb={4} />

        <Centered>
          <Avatar alt="Lucy Lavender" src="/static/img/avatars/avatar-1.jpg" />
          <Typography variant="body2" component="div" gutterBottom>
            <Box fontWeight="fontWeightMedium">Lucy Lavender</Box>
            <Box fontWeight="fontWeightRegular">Lead Developer</Box>
          </Typography>

          <Button mr={2} variant="contained" color="primary" size="small">
            Follow
          </Button>
          <Button mr={2} variant="contained" color="primary" size="small">
            Message
          </Button>
        </Centered>
      </CardContent>
    </Card>
  );
}

function Skills() {
  return (
    <Card mb={6}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Skills
        </Typography>

        <Spacer mb={4} />

        <Centered>
          <Chip size="small" mr={1} mb={1} label="HTML" color="secondary" />
          <Chip size="small" mr={1} mb={1} label="JavaScript" />
          <Chip size="small" mr={1} mb={1} label="Sass" />
          <Chip size="small" mr={1} mb={1} label="React" />
          <Chip size="small" mr={1} mb={1} label="Redux" />
          <Chip size="small" mr={1} mb={1} label="Next.js" />
          <Chip size="small" mr={1} mb={1} label="Material UI" />
          <Chip size="small" mr={1} mb={1} label="UI" />
          <Chip size="small" mr={1} mb={1} label="UX" />
        </Centered>
      </CardContent>
    </Card>
  );
}

function About() {
  return (
    <Card mb={6}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          About
        </Typography>

        <Spacer mb={4} />

        <Grid container direction="row" alignItems="center" mb={2}>
          <Grid>
            <AboutIcon>
              <Home />
            </AboutIcon>
          </Grid>
          <Grid>
            Lives in{" "}
            <Link href="https://mira.bootlab.io/">San Fransisco, SA</Link>
          </Grid>
        </Grid>
        <Grid container direction="row" alignItems="center" mb={2}>
          <Grid>
            <AboutIcon>
              <Briefcase />
            </AboutIcon>
          </Grid>
          <Grid>
            Works at <Link href="https://mira.bootlab.io/">Material UI</Link>
          </Grid>
        </Grid>
        <Grid container direction="row" alignItems="center">
          <Grid>
            <AboutIcon>
              <MapPin />
            </AboutIcon>
          </Grid>
          <Grid>
            Lives in <Link href="https://mira.bootlab.io/">Boston</Link>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

function Elsewhere() {
  return (
    <Card mb={6}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Elsewhere
        </Typography>

        <Spacer mb={4} />

        <Grid container direction="row" alignItems="center" mb={2}>
          <Grid>
            <AboutIcon>
              <ExternalLink />
            </AboutIcon>
          </Grid>
          <Grid>
            <Link href="https://mira.bootlab.io/">lucylavender.io</Link>
          </Grid>
        </Grid>
        <Grid container direction="row" alignItems="center" mb={2}>
          <Grid>
            <AboutIcon>
              <Twitter />
            </AboutIcon>
          </Grid>
          <Grid>
            <Link href="https://mira.bootlab.io/">Twitter</Link>
          </Grid>
        </Grid>
        <Grid container direction="row" alignItems="center" mb={2}>
          <Grid>
            <AboutIcon>
              <Facebook />
            </AboutIcon>
          </Grid>
          <Grid>
            <Link href="https://mira.bootlab.io/">Facebook</Link>
          </Grid>
        </Grid>
        <Grid container direction="row" alignItems="center">
          <Grid>
            <AboutIcon>
              <Instagram />
            </AboutIcon>
          </Grid>
          <Grid>
            <Link href="https://mira.bootlab.io/">Instagram</Link>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

function Earnings() {
  return (
    <Box position="relative">
      <Card mb={6} pt={2}>
        <CardContent>
          <Typography variant="h2" gutterBottom>
            <Box fontWeight="fontWeightRegular">$ 2.405</Box>
          </Typography>
          <Typography variant="body2" gutterBottom mt={3} mb={0}>
            Total Earnings
          </Typography>

          <StatsIcon>
            <DollarSign />
          </StatsIcon>
          <LinearProgress
            variant="determinate"
            value={75}
            color="secondary"
            mt={4}
          />
        </CardContent>
      </Card>
    </Box>
  );
}

function Orders() {
  return (
    <Box position="relative">
      <Card mb={6} pt={2}>
        <CardContent>
          <Typography variant="h2" gutterBottom>
            <Box fontWeight="fontWeightRegular">30</Box>
          </Typography>
          <Typography variant="body2" gutterBottom mt={3} mb={0}>
            Orders Today
          </Typography>

          <StatsIcon>
            <ShoppingBag />
          </StatsIcon>
          <LinearProgress
            variant="determinate"
            value={30}
            color="secondary"
            mt={4}
          />
        </CardContent>
      </Card>
    </Box>
  );
}

function Revenue() {
  return (
    <Box position="relative">
      <Card mb={6} pt={2}>
        <CardContent>
          <Typography variant="h2" gutterBottom>
            <Box fontWeight="fontWeightRegular">$ 1.224</Box>
          </Typography>
          <Typography variant="body2" gutterBottom mt={3} mb={0}>
            Total Revenue
          </Typography>

          <StatsIcon>
            <DollarSign />
          </StatsIcon>
          <LinearProgress
            variant="determinate"
            value={50}
            color="secondary"
            mt={4}
          />
        </CardContent>
      </Card>
    </Box>
  );
}

function Products() {
  return (
    <Card mb={6}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Products
        </Typography>
        <TableWrapper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Tech</TableCell>
                <TableCell>License</TableCell>
                <TableCell>Sales</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row">
                  AppStack
                </TableCell>
                <TableCell>
                  <ProductsChip size="small" label="HTML" color="primary" />
                </TableCell>
                <TableCell>Single License</TableCell>
                <TableCell>76</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  Mira
                </TableCell>
                <TableCell>
                  <ProductsChip size="small" label="React" color="success" />
                </TableCell>
                <TableCell>Single License</TableCell>
                <TableCell>38</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  Milo
                </TableCell>
                <TableCell>
                  <ProductsChip size="small" label="HTML" color="primary" />
                </TableCell>
                <TableCell>Single License</TableCell>
                <TableCell>43</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  Robust UI Kit
                </TableCell>
                <TableCell>
                  <ProductsChip size="small" label="Angular" color="error" />
                </TableCell>
                <TableCell>Single License</TableCell>
                <TableCell>27</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  Spark
                </TableCell>
                <TableCell>
                  <ProductsChip size="small" label="React" color="success" />
                </TableCell>
                <TableCell>Single License</TableCell>
                <TableCell>12</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableWrapper>
      </CardContent>
    </Card>
  );
}

const SalesRevenue = withTheme(({ theme }: ThemeProps) => {
  const data = {
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec"
    ],
    datasets: [
      {
        label: "Sales",
        backgroundColor: theme.palette.secondary.main,
        borderColor: theme.palette.secondary.main,
        hoverBackgroundColor: theme.palette.secondary.main,
        hoverBorderColor: theme.palette.secondary.main,
        data: [54, 67, 41, 55, 62, 45, 55, 73, 60, 76, 48, 79],
        barPercentage: 0.625,
        categoryPercentage: 0.5
      },
      {
        label: "Revenue",
        backgroundColor: theme.palette.grey[200],
        borderColor: theme.palette.grey[200],
        hoverBackgroundColor: theme.palette.grey[200],
        hoverBorderColor: theme.palette.grey[200],
        data: [69, 66, 24, 48, 52, 51, 44, 53, 62, 79, 51, 68],
        barPercentage: 0.625,
        categoryPercentage: 0.5
      }
    ]
  };

  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        grid: {
          display: false
        },
        stacked: false
      },

      x: {
        stacked: false,
        grid: {
          color: "transparent"
        }
      }
    }
  };

  return (
    <Card mb={6}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Sales / Revenue
        </Typography>

        <Spacer mb={6} />

        <ChartWrapper>
          <Bar data={data} options={options} />
        </ChartWrapper>
      </CardContent>
    </Card>
  );
});

const OnboardingStepper: React.FC<any> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set<number>());
  const [selectedPrompts, setSelectedPrompts] = useState<IPrompt[]>([]);
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const maxUploadFiles = 1;
  const [maxFilesReached, setMaxFilesReached] = useState<boolean>(false);
  const [allFiles, setAllFiles] = useState<File[]>([]);
  const [disableNewUploads, setDisableNewUploads] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<any>("");
  const [uploadsProgress, setUploadsProgress] = useState<
    {
      filename: string;
      progress: number;
    }[]
  >([]);
  const [isCreatingPrompts, setIsCreatingPrompts] = useState<boolean>(false);

  const { data: existingPrompts } = useGetPromptsQuery();
  const { data: defaultPrompts } = useGetDefaultPromptsQuery();

  const [
    addContract,
    {
      isLoading: addingContract,
      isSuccess: contractAdded,
      data: addedContract,
      reset: resetContractAdded,
      isError: errorWhileAddingContract,
      error: addedContractError
    }
  ] = useAddContractMutation();

  const [
    addDocument,
    {
      isLoading: addingDocument,
      isSuccess: documentAdded,
      data: addedDocument,
      reset: resetDocumentAdd,
      isError: errorWhileAddingDocument,
      error: addedDocumentError
    }
  ] = useAddDocumentMutation();

  const [
    uploadDocumentContent,
    {
      isLoading: isUploading,
      isSuccess: documentContentStored,
      reset: resetContent,
      isError: errorWhileUploading,
      error: uploadError
    }
  ] = useAddDocumentContentMutation();

  const [addPrompt] = useAddPromptMutation();

  const steps = [
    t("firstTimeLoginDialog.step_uploadContract", {
      count: allFiles.length,
      max: maxUploadFiles
    }),
    t("firstTimeLoginDialog.step_selectPrompts", {
      count: selectedPrompts.length,
      max: user?.account?.promptsLibraryLimit || 0
    }),
    t("firstTimeLoginDialog.step_reviewAndImport")
  ];

  useEffect(() => {
    if (
      (user?.account?.contractsRemaining &&
        user?.account.contractsRemaining - allFiles?.length < 0) ||
      (user?.account?.contractsRemaining === 0 && allFiles.length === 0)
    ) {
      setDisableNewUploads(true);
    } else {
      setDisableNewUploads(false);
    }
  }, [user?.account, allFiles]);

  useEffect(() => {
    if (allFiles?.length > maxUploadFiles) {
      setMaxFilesReached(true);
    } else {
      setMaxFilesReached(false);
    }
  }, [allFiles]);

  useEffect(() => {
    // console.log(filesToUpload);
    // console.log(documentsToImport);

    const newAllFiles = [...filesToUpload];

    if (newAllFiles?.length > maxUploadFiles) {
      const filesToRemove = newAllFiles.slice(maxUploadFiles);

      // remove these files from the filesToUpload or documentsToImport
      const newFilesToUpload = filesToUpload.filter(
        (f) => !filesToRemove.includes(f)
      );

      setFilesToUpload(newFilesToUpload);
    } else {
      setAllFiles(newAllFiles);
    }

    setAllFiles(newAllFiles);
  }, [filesToUpload]);

  const onTypeError = (error: any) => {
    setErrorType(error);
    console.log(error);
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      createPrompts().then(() => {
        uploadFiles();
      });
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleUploads = async (moreFiles: any) => {
    if (!moreFiles[0].origin || moreFiles[0].origin === "upload") {
      setFilesToUpload([...filesToUpload, ...moreFiles]);
    }
  };

  const deleteFileToUpload = (fileToDelete: File) => {
    // remove the files from filesToUpload
    const newFilesToUpload = filesToUpload.filter(
      (f) => f.name !== fileToDelete.name && f.size !== fileToDelete.size
    );

    setFilesToUpload([...newFilesToUpload]);
    // the file will be automatically removed from the allFiles by the useEffect
  };

  const createPrompts = async () => {
    if (!defaultPrompts || selectedPrompts.length === 0) {
      return;
    }

    setIsCreatingPrompts(true);

    const createdPrompts = await Promise.all(
      defaultPrompts
        .filter(
          (d) => existingPrompts?.find((e) => e.id === d.id) === undefined
        )
        .map((prompt) => {
          return addPrompt({
            title: prompt.title,
            body: prompt.body,
            author: user.id,
            enabled: selectedPrompts.find((s) => s.id === prompt.id)
              ? true
              : false,
            hidden: false
          }).unwrap();
        })
    )
      .then((results) => {
        return results;
      })
      .catch((error: any) => {
        console.error("Error creating prompts:", error);
        return [];
      });

    console.log("Created prompts:", createdPrompts);
    setIsCreatingPrompts(false);
  };

  const uploadFiles = async () => {
    const promises: PromiseLike<boolean>[] = [];
    filesToUpload?.forEach((f: File, i: number) => {
      promises.push(uploadFile(f, i));
    });

    await Promise.all(promises)
      .then(() => {
        // all done, close the dialog
      })
      .catch((error: any) => {
        console.error(error);
      });
  };

  const handleSuccess = () => {
    // navigate to the contract page
    if (addedContract) {
      navigate(`/contract/${addedContract.id}`);
    }

    onClose();
    setFilesToUpload([]);
    setAllFiles([]);
    setUploadsProgress([]);
  };

  const handleProgress = (
    filename: string,
    progress: number,
    index: number // pass the index in case there are documents with the same filename
  ) => {
    setUploadsProgress((prev: any) => {
      if (
        !prev.some((p: any) => p.filename === filename && p.index === index)
      ) {
        // initiate the progress
        return [...prev, { filename, progress, index }];
      } else {
        // update the progress
        const updatedProgress = prev.map((p: any) => {
          if (p.filename === filename && p.index === index) {
            p.progress = progress;
          }
          return p;
        });

        return updatedProgress;
      }
    });
  };

  const uploadFile = (f: File, index: number): PromiseLike<boolean> => {
    return new Promise((resolve, reject) => {
      handleProgress(f.name, 10, index);

      // create a contract for the file
      const newContract: any = {
        status: ContractStatus.PUBLISHED, // an imported contract is always published, drafts are new files that are created by the user or generated by AI
        name: f.name,
        ownerIds: [user.id],
        managerIds: [user.id]
      };

      addContract(newContract)
        .unwrap()
        .then((contract_payload) => {
          handleProgress(f.name, 40, index);

          // create a document for the file
          const newDocument: IDocument = {
            name: f.name,
            type: "contract",
            origin: "upload",
            location: "blobstorage",
            size: f.size,
            contractId: contract_payload.id
          };

          addDocument(newDocument)
            .unwrap()
            .then((document_payload) => {
              handleProgress(f.name, 50, index);

              // upload the file content
              uploadLocalFileContentToAzureBlobStorage(
                f,
                document_payload,
                contract_payload
              )
                .then(() => {
                  handleProgress(f.name, 100, index);

                  resolve(true);
                })
                .catch((error: any) => {
                  reject(error);
                });
            })
            .catch((error: any) => {
              reject(error);
            });
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  };

  const uploadLocalFileContentToAzureBlobStorage = async (
    fileToUpload: File,
    document: IDocument,
    contract: IContract
  ) => {
    if (document?.origin === "upload") {
      const formData = new FormData();
      formData.append("file", fileToUpload);
      await uploadDocumentContent({
        contractId: contract.id,
        documentId: document.id,
        file: formData
      });
    }
  };

  const handleClose = (
    event: {},
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      // Prevent closing the dialog on backdrop click or escape key
      return;
    }

    onClose();
  };

  const onClose = () => {
    setOpen(false);
    setFilesToUpload([]);
    setAllFiles([]);
    setUploadsProgress([]);
    setSelectedPrompts([]);
    setActiveStep(0);
    setErrorType("");
    resetContractAdded();
    resetDocumentAdd();
    resetContent();
  };

  return (
    <Box position="relative">
      <Card mb={6} pt={2}>
        <CardContent>
          {user?.account === undefined && <CircularProgress color="primary" />}
          <Stepper activeStep={activeStep}>
            {steps.map((label, index) => {
              return (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>

          {activeStep === 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom color="textSecondary">
                {t("firstTimeLoginDialog.step_uploadContract_description")}
              </Typography>
              {/* <Alert severity="info" sx={{ mt: 2 }}>
                {t("firstTimeLoginDialog.step_uploadContract_description")}
              </Alert> */}
              {errorType ? (
                <Typography mt={2} mb={1} variant="subtitle1" color="error">
                  {errorType}
                </Typography>
              ) : (
                <></>
              )}
              {filesToUpload?.length > 0 ? (
                <FilesToUploadList
                  mode={DialogMode.Add}
                  filesWithProgress={allFiles
                    .filter((f) => f instanceof File)
                    .map((f: File, i: number) => {
                      const progress = uploadsProgress.find(
                        (p) => p.filename === f.name
                      );
                      return { file: f, progress: progress?.progress || 0 };
                    })}
                  handleDeleteFileToUpload={(index: number) =>
                    deleteFileToUpload(allFiles[index])
                  }
                  documents={[]}
                />
              ) : (
                <ManualFileUploader
                  onChange={handleUploads}
                  disabled={disableNewUploads}
                  onTypeError={onTypeError}
                  maxUploadFiles={maxUploadFiles}
                  maxFilesReached={filesToUpload.length >= 1}
                  disableNewUploads={disableNewUploads}
                />
              )}
            </Box>
          ) : activeStep === 1 ? (
            <PromptsSelector
              defaultPrompts={defaultPrompts}
              selectedPrompts={selectedPrompts}
              setSelectedPrompts={setSelectedPrompts}
            />
          ) : activeStep === 2 ? (
            <Stack sx={{ mt: 2 }} spacing={4}>
              <Alert severity="info" sx={{ my: 2 }}>
                {t("firstTimeLoginDialog.uploadedContractDescription")}
              </Alert>

              <div>
                <Typography variant="body1">{t("Contract")}:</Typography>

                <List sx={{ width: "100%" }} disablePadding dense>
                  {allFiles?.map((f: File, i: number) => (
                    <Typography fontWeight={700}>{f.name}</Typography>
                  ))}
                </List>
              </div>

              <div>
                <Typography sx={{ mt: 2, mb: 1 }}>{t("Prompts")}:</Typography>

                <Stack direction="row" gap={2} flexWrap="wrap">
                  {selectedPrompts?.map((p) => (
                    <Chip label={t(p.title || "")} sx={{ fontWeight: 700 }} />
                  ))}
                </Stack>
              </div>
            </Stack>
          ) : (
            <React.Fragment>
              <Typography sx={{ mt: 2, mb: 1 }}>
                Step {activeStep + 1}
              </Typography>
            </React.Fragment>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

function Onboarding() {
  const { t } = useTranslation();

  return (
    <React.Fragment>
      <Helmet title={t("Onboarding")!} />

      <BreadCrumbsBar mt={2}>
        <Link component={NavLink} to="/pages/onboarding" color="inherit">
          <Typography variant="h3" gutterBottom display="inline">
            {t("Onboarding")}
          </Typography>
        </Link>
      </BreadCrumbsBar>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid size={{ xs: 12, lg: 4, xl: 3 }}>
          <FirstTimeLoginDialog mode={DialogMode.Add} />
        </Grid>
        <Grid size={{ xs: 12, lg: 8, xl: 9 }}>
          <SalesRevenue />
          <Grid container spacing={6}>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Earnings />
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Orders />
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Revenue />
            </Grid>
          </Grid>
          <Products />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default Onboarding;
